import type { RxDocument } from 'rxdb';

import type { Block, Instruction, Note, UserSource } from '@flintmint/core-shared/rxdb';
import type { AgentEditScope } from './use-llm-agent';

export type BlockInstruction = {
  blockId: string;
  instruction: RxDocument<Instruction> | Instruction;
};

type LLMDocument = RxDocument<Note> | RxDocument<Block> | Note | Block;

export type LLMDocumentContext = {
  document: LLMDocument;
  documentInstructions?: Array<RxDocument<Instruction> | Instruction>;
  blocksInstructions?: BlockInstruction[];
};

export type LLMRequestContext = LLMDocumentContext & {
  userSources?: Array<RxDocument<UserSource> | UserSource>;
};

type InvocationVariables = Record<string, any>;

type PromptInvocation = {
  variables: InvocationVariables;
};

export type GenerateDocumentNameVariables = {
  currentDate: string;
  content: string;
  documentName: string | null;
  documentInstructions?: Array<{ name: string; content: string }>;
  userMessages?: string[];
};

export const buildGenerateTextVariables = (userPrompt: string, context?: LLMDocumentContext): PromptInvocation => ({
  variables: {
    ...buildBaseInput(context),
    userPrompt,
  },
});

export const buildGenerateDocumentNameVariables = (
  content: string,
  context: Pick<LLMDocumentContext, 'documentInstructions'> & { documentName: string | null; userMessages?: string[] }
): { variables: GenerateDocumentNameVariables } => {
  const { documentName, documentInstructions = [], userMessages = [] } = context;
  const variables: GenerateDocumentNameVariables = {
    currentDate: getCurrentDate(),
    content,
    documentName,
  };

  if (documentInstructions.length) variables.documentInstructions = mapInstructions(documentInstructions);
  if (userMessages.length) variables.userMessages = userMessages;

  return { variables };
};

export const serializeUserSources = (sources: Array<RxDocument<UserSource> | UserSource> = []): UserSource[] =>
  sources.map((source) => ('toMutableJSON' in source ? source.toMutableJSON() : source));

const buildBaseInput = (context?: LLMDocumentContext) => {
  const input: InvocationVariables = {
    currentDate: getCurrentDate(),
  };
  if (!context) return input;

  const { document, documentInstructions = [], blocksInstructions = [] } = context;
  Object.assign(input, extractDocumentInfo(document));
  if (documentInstructions.length) input.documentInstructions = mapInstructions(documentInstructions);
  if (blocksInstructions.length) input.blocksInstructions = mapBlocksInstructions(blocksInstructions);

  return input;
};

const extractDocumentInfo = (document?: LLMDocumentContext['document']) => {
  if (!document) return {};

  return {
    documentName: document.name,
    documentContent: document.content,
  };
};

const mapInstructions = (instructions: Array<RxDocument<Instruction> | Instruction> = []) =>
  instructions.map((instruction) => ({
    name: instruction.name,
    content: instruction.content,
  }));

const mapBlocksInstructions = (blocksInstructions: BlockInstruction[] = []) =>
  blocksInstructions.map(({ blockId, instruction }) => ({
    blockId,
    instruction: {
      name: instruction.name,
      content: instruction.content,
    },
  }));

export type AgentInstructionBodyFields = {
  documentInstructions?: Array<{ name: string; content: string }>;
  blocksInstructions?: Array<{ blockId: string; instruction: { name: string; content: string } }>;
};

export const buildAgentInstructionBodyFields = (context: LLMDocumentContext): AgentInstructionBodyFields => {
  const { documentInstructions = [], blocksInstructions = [] } = context;
  const fields: AgentInstructionBodyFields = {};

  if (documentInstructions.length) fields.documentInstructions = mapInstructions(documentInstructions);
  if (blocksInstructions.length) fields.blocksInstructions = mapBlocksInstructions(blocksInstructions);

  return fields;
};

export type AgentContext = {
  currentDate: string;
  documentName?: string;
  schemaAwareness?: string;
  scopedEdit?: {
    mode: 'targeted';
    instructions: string[];
    toolBehavior: string[];
  };
};

const buildScopedEditContext = (): NonNullable<AgentContext['scopedEdit']> => ({
  mode: 'targeted',
  instructions: [
    'This request is scoped to targeted content rather than the whole document.',
    'You may read other parts of the document for context and cohesion.',
    'You must only modify the targeted content.',
    'Do not append below the targeted content, insert outside it, or make document-wide changes unless the user explicitly asks.',
    'Use replaceSelection for all writes in targeted mode.',
    'Provide only the replacement fragment for the selected content, not the surrounding paragraph or document.',
  ],
  toolBehavior: [
    'Prefer reading the targeted content first.',
    'Use broader document reads only when needed for coherence.',
    'Treat surrounding content as read-only context unless the user explicitly broadens the scope.',
    'Do not reread the document or selection after a successful write solely to verify your own edit.',
    'If a targeted write fails, follow the tool output and adapt.',
  ],
});

export const buildAgentDocumentContext = (
  context: LLMDocumentContext,
  schemaAwareness?: string,
  editScope: AgentEditScope = 'document'
): AgentContext => {
  const result: AgentContext = {
    schemaAwareness,
    currentDate: getCurrentDate(),
    documentName: context.document?.name,
  };

  if (editScope === 'targeted') {
    result.scopedEdit = buildScopedEditContext();
  }

  return result;
};

const getCurrentDate = () => new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
